import {
  Box,
  FormHelperText,
  LinearProgress,
  makeStyles,
  Typography,
} from '@material-ui/core';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { cognitoSessionToTokenSession } from '~/src/utils/cognitoUtils';
import GoogleIcon from '../../../../public/icons/google.svg';
import { Button, Form, Link, TextField } from '../../../components/styled';
import { SsoAccount, useAwsApi, useLoginApi } from '../../../hooks';
import { SignInActionButton } from '../SignInActionButton';

/**
 * email: ask for the email and look up how it signs in.
 * sso: the email routes to single sign-on and there is a choice to make: several accounts, or a
 *   Google or password login as well.
 * password: the email signs in with a password.
 */
type Step = 'email' | 'sso' | 'password';

/**
 * /auth/login?accountId=… falls back to this page, keeping accountId in the query, when the
 * account has no SSO provider: that email can only sign in with a password.
 */
function initialStep(): Step {
  return new URLSearchParams(window.location.search).has('accountId')
    ? 'password'
    : 'email';
}

/** The account-pinned login route, carrying the page's own query (destination, appSubdomain). */
function ssoLoginUrl(accountId: string): string {
  const params = new URLSearchParams(window.location.search);
  params.set('accountId', accountId);
  return `/auth/login?${params.toString()}`;
}

const useStyles = makeStyles(() => ({
  // "or" between the Google button and the form: a caption with a rule on each side.
  divider: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 20,
    color: 'var(--fg-muted)',
    fontSize: 'var(--text-caption)',
    lineHeight: 'var(--text-caption--line-height)',
    '&::before, &::after': {
      content: '""',
      flex: 1,
      borderTop: '1px solid var(--border)',
    },
    '&::before': {
      marginRight: 12,
    },
    '&::after': {
      marginLeft: 12,
    },
  },
  identity: {
    marginTop: 20,
    fontSize: 'var(--text-label)',
    lineHeight: 'var(--text-label--line-height)',
    fontWeight: 500,
    color: 'var(--fg-heading)',
    overflowWrap: 'anywhere',
  },
  hint: {
    marginTop: 12,
    fontSize: 'var(--text-caption)',
    lineHeight: 'var(--text-caption--line-height)',
    color: 'var(--fg-muted)',
  },
  hintLink: {
    color: 'var(--fg-heading)',
    fontWeight: 500,
    textDecoration: 'none',
    textUnderlineOffset: '4px',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  // Text buttons carry 8px of horizontal padding; pull the row out so the labels sit on the
  // column edges like the links and fields do.
  textActions: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '0 -8px',
  },
}));

export function EmailLogin() {
  const classes = useStyles();
  const [step, setStep] = React.useState<Step>(initialStep);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [ssoAccounts, setSsoAccounts] = React.useState<SsoAccount[]>([]);
  const [hasDirectSignIn, setHasDirectSignIn] = React.useState(false);
  const [isLoading, setLoading] = React.useState(false);
  const [promptForMfaCode, setPromptForMfaCode] = React.useState(false);
  const [mfaCode, setMfaCode] = React.useState('');
  const [error, setError] = React.useState('');
  const { loginWithCredentials, sendMfaCode } = useAwsApi();
  const { lookup, setSession } = useLoginApi();

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setError('');
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setError('');
  };

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { ssoAccounts: accounts, hasDirectSignIn: direct } =
        await lookup(email);
      // A session created through an IdP can only use that IdP's accounts, so someone who can
      // also sign in directly gets to choose; everyone else with one SSO account goes straight on.
      if (accounts.length === 1 && !direct) {
        document.location.assign(ssoLoginUrl(accounts[0].accountId));
        return;
      }

      setSsoAccounts(accounts);
      setHasDirectSignIn(Boolean(direct));
      setStep(accounts.length === 0 ? 'password' : 'sso');
    } catch (err) {
      // The lookup is a convenience: a password still works without it.
      setStep('password');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendVerification = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const session = await sendMfaCode(mfaCode);
      const tokenSession = cognitoSessionToTokenSession(session);

      const { redirect } = await setSession({
        session: tokenSession,
      });

      document.location.assign(
        redirect ?? `/account-selector${window.location.search}`,
      );
    } catch (err) {
      setError('MFA Code not valid. Try again.');
      setLoading(false);
    }
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      // The identifier goes to Cognito exactly as typed: usernames are case-sensitive.
      const { session, totp } = await loginWithCredentials(email, password);
      if (!session) {
        // TODO: add email challenge checker
        if (totp.challengeName === 'SOFTWARE_TOKEN_MFA') {
          setLoading(false);
          setPromptForMfaCode(true);
          return;
        }
        throw new Error('Unrecognized login error');
      }

      const tokenSession = cognitoSessionToTokenSession(session);

      const { redirect } = await setSession({
        session: tokenSession,
      });

      document.location.assign(
        redirect ?? `/account-selector${window.location.search}`,
      );
    } catch (err) {
      setError('Unable to login. Try again or contact support.');
      setLoading(false);
    }
  };

  const wrapHandleFormSubmit = (event: React.FormEvent) => {
    handleFormSubmit(event).catch((err) => {
      setError(err.message);
    });
  };

  const wrapHandleEmailSubmit = (event: React.FormEvent) => {
    handleEmailSubmit(event).catch((err) => {
      setError(err.message);
      setLoading(false);
    });
  };

  const backToEmail = () => {
    setSsoAccounts([]);
    setHasDirectSignIn(false);
    setPassword('');
    setError('');
    setStep('email');
  };

  const renderGoogleButton = () => (
    <SignInActionButton
      label="Sign in with Google"
      icon={GoogleIcon}
      id="sign-in-with-google"
      onClick={() =>
        document.location.replace(
          `/auth/google/authorize${window.location.search}`,
        )
      }
    />
  );

  const renderEmailStep = () => (
    <>
      {renderGoogleButton()}
      <div className={classes.divider}>or</div>
      <Form onSubmit={wrapHandleEmailSubmit} id="login-email">
        <TextField
          type="email"
          label="Email"
          autoComplete="email"
          onChange={handleEmailChange}
          value={email}
          autoFocus
        />
        <Button disabled={!email || isLoading}>Next</Button>
      </Form>
    </>
  );

  const renderSsoStep = () => (
    <Box display="flex" flexDirection="column">
      <Typography className={classes.identity}>{email}</Typography>
      <Typography className={classes.hint}>
        {ssoAccounts.length > 1
          ? 'This email can sign in to more than one organization. Choose one to continue.'
          : 'Your organization uses single sign-on.'}
      </Typography>
      {ssoAccounts.map((account) => (
        <Button
          key={account.accountId}
          type="button"
          onClick={() =>
            document.location.assign(ssoLoginUrl(account.accountId))
          }
        >
          {ssoAccounts.length > 1
            ? (account.accountName ?? account.accountId)
            : 'Continue with single sign-on'}
        </Button>
      ))}
      <Box className={classes.textActions}>
        {hasDirectSignIn && (
          <Button
            type="button"
            variant="text"
            fullWidth={false}
            onClick={() => setStep('password')}
          >
            Sign in another way
          </Button>
        )}
        <Button
          type="button"
          variant="text"
          fullWidth={false}
          onClick={backToEmail}
        >
          Use a different email
        </Button>
      </Box>
    </Box>
  );

  const renderPasswordStep = () => (
    <>
      {renderGoogleButton()}
      <div className={classes.divider}>or</div>
      <Form onSubmit={wrapHandleFormSubmit} id="login-sign-in">
        <TextField
          type="email"
          label="Email"
          autoComplete="email"
          onChange={handleEmailChange}
          value={email}
          autoFocus={!email}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          onChange={handlePasswordChange}
          value={password}
          autoFocus={!!email}
        />
        <Button disabled={!email || !password || isLoading}>Sign In</Button>
      </Form>
      <Typography className={classes.hint}>
        No password? Your organization may use single sign-on.{' '}
        <RouterLink to="/forgot-sso" className={classes.hintLink}>
          Email me links to my accounts
        </RouterLink>
      </Typography>
      <Box className={classes.textActions}>
        <Button
          type="button"
          variant="text"
          fullWidth={false}
          onClick={backToEmail}
        >
          Back
        </Button>
      </Box>
    </>
  );

  const renderStep = () => {
    if (step === 'email') return renderEmailStep();
    if (step === 'sso') return renderSsoStep();
    return renderPasswordStep();
  };

  return (
    <>
      {isLoading && <LinearProgress />}
      {promptForMfaCode ? (
        <Box display="flex" flexDirection="column">
          <Typography variant="subtitle1">
            Enter your MFA verification code from your Authenticator app:
          </Typography>
          <Form onSubmit={sendVerification} id="login-mfa">
            <TextField
              label="MFA Code"
              onChange={(e) => setMfaCode(e.target.value)}
              value={mfaCode}
            />
            <Button disabled={!mfaCode || isLoading}>Submit</Button>
          </Form>
          <Typography variant="subtitle2">
            Lost access to your Authenticator app? Contact an account
            administrator to reset your MFA.
          </Typography>
          <Button onClick={() => setPromptForMfaCode(false)}>Back</Button>
        </Box>
      ) : (
        renderStep()
      )}
      {error && <FormHelperText error>{error}</FormHelperText>}
      {!promptForMfaCode && step !== 'sso' && (
        <Box className={classes.actions}>
          <Link to="/forgot-password">Forgot Password?</Link>
          <Link to="/sign-up">Sign up</Link>
        </Box>
      )}
    </>
  );
}
